import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ApiError,
  Button,
  Card,
  Feedback,
  Field,
  maskPhoneInput,
  unmaskPhone,
  useAuthStore,
  useTenant,
} from '@barber/ui';

/**
 * Entrar — telefone, código, e cadastro se for a primeira vez.
 *
 * ---
 *
 * NÃO EXISTE SENHA EM LUGAR NENHUM
 *
 * O cliente digita o telefone, recebe um código e entra. Isso elimina a tela de
 * "esqueci minha senha", o vazamento de credenciais e, principalmente, o
 * abandono: ninguém desiste de agendar porque não lembra da senha.
 *
 * A tela NÃO diferencia telefone cadastrado de não cadastrado antes do código —
 * o servidor responde igual para os dois. Diferenciar aqui transformaria o
 * formulário num verificador de cadastro: daria para descobrir quem é cliente
 * da barbearia testando números.
 */

type Etapa = 'telefone' | 'codigo' | 'cadastro';

const TEMPO_REENVIO = 30;

export function LoginPage() {
  const navigate = useNavigate();
  const { tenant } = useTenant();

  const requestOtp = useAuthStore((s) => s.requestOtp);
  const verifyOtp = useAuthStore((s) => s.verifyOtp);
  const register = useAuthStore((s) => s.register);

  const [etapa, setEtapa] = useState<Etapa>('telefone');
  const [telefone, setTelefone] = useState('');
  const [codigo, setCodigo] = useState('');
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [segundos, setSegundos] = useState(0);

  const campoCodigo = useRef<HTMLInputElement>(null);
  const campoNome = useRef<HTMLInputElement>(null);

  // Contagem para liberar o reenvio. O servidor também impõe o intervalo — isto
  // aqui evita que o cliente descubra o limite levando um erro na cara.
  useEffect(() => {
    if (segundos <= 0) return;
    const timer = setTimeout(() => setSegundos((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [segundos]);

  useEffect(() => {
    if (etapa === 'codigo') campoCodigo.current?.focus();
    if (etapa === 'cadastro') campoNome.current?.focus();
  }, [etapa]);

  function mostrarErro(error: unknown): void {
    setErro(
      error instanceof ApiError ? error.message : 'Algo deu errado. Tente novamente em instantes.',
    );
  }

  async function pedirCodigo(): Promise<void> {
    setErro(null);
    setEnviando(true);

    try {
      const resultado = await requestOtp(unmaskPhone(telefone));

      setEtapa('codigo');
      setSegundos(TEMPO_REENVIO);
      setAviso(
        resultado.devCode
          ? `Ambiente de desenvolvimento: o código é ${resultado.devCode}.`
          : `Enviamos um código por ${resultado.channel === 'SMS' ? 'SMS' : 'WhatsApp'} para ${resultado.maskedPhone}.`,
      );
    } catch (error) {
      mostrarErro(error);
    } finally {
      setEnviando(false);
    }
  }

  async function conferirCodigo(): Promise<void> {
    setErro(null);
    setEnviando(true);

    try {
      const resultado = await verifyOtp(codigo);

      if (resultado === 'registration_required') {
        setEtapa('cadastro');
        setAviso('Telefone confirmado! Falta só o seu nome.');
        return;
      }

      navigate('/', { replace: true });
    } catch (error) {
      mostrarErro(error);
    } finally {
      setEnviando(false);
    }
  }

  async function concluirCadastro(): Promise<void> {
    setErro(null);
    setEnviando(true);

    try {
      await register({ name: nome, ...(email.trim() ? { email: email.trim() } : {}) });
      navigate('/', { replace: true });
    } catch (error) {
      mostrarErro(error);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-lg flex-col justify-center gap-6 px-5 py-10">
      <header className="text-center">
        {tenant.branding.logoUrl ? (
          <img
            src={tenant.branding.logoUrl}
            alt={tenant.name}
            className="mx-auto h-16 w-16 rounded-2xl object-cover"
          />
        ) : (
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-brand text-xl font-bold text-brand-fg">
            {tenant.name.slice(0, 2).toUpperCase()}
          </span>
        )}

        <h1 className="mt-4 text-2xl font-bold text-content">{tenant.name}</h1>
        <p className="mt-1 text-sm text-content-muted">
          {etapa === 'telefone' && 'Entre com seu telefone para agendar'}
          {etapa === 'codigo' && 'Digite o código que enviamos'}
          {etapa === 'cadastro' && 'Falta pouco para o seu primeiro horário'}
        </p>
      </header>

      <Card className="flex flex-col gap-4">
        {etapa === 'telefone' && (
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              void pedirCodigo();
            }}
          >
            <Field
              label="Seu telefone"
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              placeholder="(11) 98765-4321"
              value={telefone}
              onChange={(event) => setTelefone(maskPhoneInput(event.target.value))}
              hint="Você vai receber um código por WhatsApp."
            />

            <Button type="submit" fullWidth loading={enviando} disabled={telefone.length < 14}>
              Receber código
            </Button>
          </form>
        )}

        {etapa === 'codigo' && (
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              void conferirCodigo();
            }}
          >
            <Field
              ref={campoCodigo}
              label="Código"
              type="text"
              inputMode="numeric"
              // `one-time-code` faz o iOS e o Android oferecerem o código do SMS
              // no teclado. Sem isso, o cliente sai do app para copiar — e uma
              // parte não volta.
              autoComplete="one-time-code"
              placeholder="000000"
              maxLength={8}
              value={codigo}
              onChange={(event) => setCodigo(event.target.value.replace(/\D/g, ''))}
            />

            <Button type="submit" fullWidth loading={enviando} disabled={codigo.length < 4}>
              Entrar
            </Button>

            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                className="tap-target text-content-muted underline"
                onClick={() => {
                  setEtapa('telefone');
                  setCodigo('');
                  setAviso(null);
                }}
              >
                Trocar telefone
              </button>

              <button
                type="button"
                className="tap-target text-brand disabled:text-content-muted"
                disabled={segundos > 0 || enviando}
                onClick={() => void pedirCodigo()}
              >
                {segundos > 0 ? `Reenviar em ${segundos}s` : 'Reenviar código'}
              </button>
            </div>
          </form>
        )}

        {etapa === 'cadastro' && (
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              void concluirCadastro();
            }}
          >
            <Field
              ref={campoNome}
              label="Como podemos te chamar?"
              autoComplete="name"
              placeholder="Seu nome"
              value={nome}
              onChange={(event) => setNome(event.target.value)}
            />

            <Field
              label="E-mail (opcional)"
              type="email"
              autoComplete="email"
              placeholder="voce@email.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              hint="Só usamos para recibo e avisos importantes."
            />

            <Button type="submit" fullWidth loading={enviando} disabled={nome.trim().length < 2}>
              Criar minha conta
            </Button>
          </form>
        )}

        {aviso && !erro && <Feedback tone="success">{aviso}</Feedback>}
        {erro && <Feedback tone="error">{erro}</Feedback>}
      </Card>

      <p className="text-center text-xs text-content-muted">
        Ao continuar, você concorda em receber mensagens sobre seus agendamentos.
      </p>
    </div>
  );
}
